import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { verifyPasswordToken, setPassword } from '../services/resortAdminApi';
import { CheckCircle, XCircle, Lock } from 'lucide-react';

export const SetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenInfo, setTokenInfo] = useState<{ email?: string; restaurant?: string; grocery?: string } | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);
  
  const [passwordForm, setPasswordForm] = useState({
    password: '',
    confirmPassword: '',
  });

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError('No token provided');
        setLoading(false);
        return;
      }
      
      try {
        const result = await verifyPasswordToken(token);
        if (result.valid) {
          setTokenValid(true);
          setTokenInfo({
            email: result.email,
            restaurant: result.restaurant,
            grocery: result.grocery,
          });
        } else {
          setError(result.error || 'Invalid or expired token');
        }
      } catch (error: any) {
        setError(error.message || 'Failed to verify token');
      } finally {
        setLoading(false);
      }
    };
    
    verifyToken();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!passwordForm.password || !passwordForm.confirmPassword) {
      setError('All fields are required');
      return;
    }
    
    if (passwordForm.password !== passwordForm.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (passwordForm.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }
    
    if (!token) {
      setError('No token provided');
      return;
    }
    
    setVerifying(true);
    try {
      const result = await setPassword(token, passwordForm.password);
      const target = result.redirect_url || null;
      setRedirectUrl(target);
      setSuccess(true);
      setTimeout(() => {
        if (target) {
          window.location.href = target;
        } else {
          navigate('/login');
        }
      }, 2000);
    } catch (error: any) {
      setError(error.message || 'Failed to set password. Token may be invalid or expired.');
    } finally {
      setVerifying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-alps-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Verifying token...</p>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="max-w-md w-full">
          <div className="text-center">
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-semibold text-gray-800 mb-2">Invalid Token</h1>
            <p className="text-gray-600 mb-4">{error || 'This password reset link is invalid or has expired.'}</p>
            <button
              onClick={() => navigate('/login')}
              className="px-4 py-2 bg-alps-primary text-white rounded-md hover:bg-alps-primary-dark"
            >
              Go to Login
            </button>
          </div>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="max-w-md w-full">
          <div className="text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-semibold text-gray-800 mb-2">Password Set Successfully</h1>
            <p className="text-gray-600 mb-4">
              Your password has been set. Redirecting{redirectUrl ? '' : ' to login'}...
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <Card className="max-w-md w-full">
        <div className="text-center mb-6">
          <Lock className="h-12 w-12 text-alps-primary mx-auto mb-4" />
          <h1 className="text-2xl font-semibold text-gray-800">Set Your Password</h1>
          {tokenInfo?.email && (
            <p className="mt-2 text-sm text-gray-600">
              Setting password for: <strong>{tokenInfo.email}</strong>
            </p>
          )}
          {tokenInfo?.restaurant && (
            <p className="mt-1 text-sm text-gray-600">
              Restaurant: <strong>{tokenInfo.restaurant}</strong>
            </p>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700">
              New Password
            </label>
            <input
              type="password"
              id="password"
              autoComplete="new-password"
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
              value={passwordForm.password}
              onChange={(e) =>
                setPasswordForm({ ...passwordForm, password: e.target.value })
              }
              required
              minLength={6}
            />
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
              Confirm Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm"
              value={passwordForm.confirmPassword}
              onChange={(e) =>
                setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })
              }
              required
              minLength={6}
            />
          </div>

          <button
            type="submit"
            disabled={verifying}
            className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-alps-primary hover:bg-alps-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-alps-primary disabled:opacity-50"
          >
            {verifying ? 'Setting Password...' : 'Set Password'}
          </button>
        </form>
      </Card>
    </div>
  );
};

